import { useMemo, useState } from "react";
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from "recharts";
import { TrendingUp, Scale } from "lucide-react";
import type { PR } from "@/types/evolve";
import { cn } from "@/lib/utils";

interface WeightPRGraphProps {
  prs: PR[];
  weightHistory?: { date: string; weight: number }[];
}

export function WeightPRGraph({ prs, weightHistory = [] }: WeightPRGraphProps) {
  const [selectedLifts, setSelectedLifts] = useState<Set<string>>(new Set());

  // Group PRs by lift and prepare chart data
  const prChartData = useMemo(() => {
    if (prs.length === 0) return [];
    
    // Get unique lifts
    const lifts = [...new Set(prs.map(pr => pr.lift))];
    
    // Create data points grouped by date
    const dateMap = new Map<string, Record<string, string | number>>();
    
    prs.forEach(pr => {
      const dateKey = new Date(pr.date).toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
      if (!dateMap.has(dateKey)) {
        dateMap.set(dateKey, { date: dateKey });
      }
      const entry = dateMap.get(dateKey)!;
      // Keep the highest PR for that lift on that date
      const currentWeight = entry[pr.lift] as number | undefined;
      if (!currentWeight || pr.weight > currentWeight) {
        entry[pr.lift] = pr.weight;
      }
    });
    
    return Array.from(dateMap.values()).sort((a, b) => 
      new Date(a.date).getTime() - new Date(b.date).getTime()
    );
  }, [prs]);

  const lifts = useMemo(() => [...new Set(prs.map(pr => pr.lift))], [prs]);

  const weightChartData = useMemo(() => {
    return weightHistory.map(entry => ({
      date: new Date(entry.date).toLocaleDateString('en-US', { month: 'short', day: 'numeric' }),
      weight: entry.weight
    }));
  }, [weightHistory]);

  const colors = ['hsl(var(--primary))', 'hsl(var(--chart-2))', 'hsl(var(--chart-3))', 'hsl(var(--chart-4))', 'hsl(var(--chart-5))'];

  const toggleLift = (lift: string) => {
    setSelectedLifts(prev => {
      const newSet = new Set(prev);
      if (newSet.has(lift)) {
        newSet.delete(lift);
      } else {
        newSet.add(lift);
      }
      return newSet;
    });
  };

  const isLiftVisible = (lift: string) => {
    // If nothing is selected, show all
    if (selectedLifts.size === 0) return true;
    return selectedLifts.has(lift);
  };

  if (prs.length === 0 && weightHistory.length === 0) {
    return null;
  }

  return (
    <div className="space-y-6">
      {/* Weight Tracking Graph */}
      {weightHistory.length > 0 && (
        <section className="glass-card rounded-2xl p-5 space-y-4">
          <div className="flex items-center gap-2">
            <Scale className="w-5 h-5 text-primary" />
            <h3 className="font-display text-lg font-bold">Weight Trend</h3>
          </div>
          <div className="h-48">
            <ResponsiveContainer width="100%" height="100%">
              <LineChart data={weightChartData}>
                <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" />
                <XAxis 
                  dataKey="date" 
                  tick={{ fontSize: 10, fill: 'hsl(var(--muted-foreground))' }}
                  stroke="hsl(var(--border))"
                />
                <YAxis 
                  tick={{ fontSize: 10, fill: 'hsl(var(--muted-foreground))' }}
                  stroke="hsl(var(--border))"
                  domain={['dataMin - 5', 'dataMax + 5']}
                />
                <Tooltip
                  contentStyle={{
                    backgroundColor: 'hsl(var(--card))',
                    border: '1px solid hsl(var(--border))',
                    borderRadius: '8px',
                    fontSize: '12px'
                  }}
                  labelStyle={{ color: 'hsl(var(--foreground))' }}
                />
                <Line 
                  type="monotone" 
                  dataKey="weight" 
                  stroke="hsl(var(--primary))" 
                  strokeWidth={2}
                  dot={{ fill: 'hsl(var(--primary))', strokeWidth: 2, r: 4 }}
                  activeDot={{ r: 6, fill: 'hsl(var(--primary))' }}
                />
              </LineChart>
            </ResponsiveContainer>
          </div>
        </section>
      )}

      {/* PR Progress Graph */}
      {prs.length > 1 && (
        <section className="glass-card rounded-2xl p-5 space-y-4">
          <div className="flex items-center gap-2">
            <TrendingUp className="w-5 h-5 text-primary" />
            <h3 className="font-display text-lg font-bold">PR Progress</h3>
          </div>
          <div className="h-48">
            <ResponsiveContainer width="100%" height="100%">
              <LineChart data={prChartData}>
                <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" />
                <XAxis 
                  dataKey="date" 
                  tick={{ fontSize: 10, fill: 'hsl(var(--muted-foreground))' }}
                  stroke="hsl(var(--border))"
                />
                <YAxis 
                  tick={{ fontSize: 10, fill: 'hsl(var(--muted-foreground))' }}
                  stroke="hsl(var(--border))"
                />
                <Tooltip
                  contentStyle={{
                    backgroundColor: 'hsl(var(--card))',
                    border: '1px solid hsl(var(--border))',
                    borderRadius: '8px',
                    fontSize: '12px'
                  }}
                  labelStyle={{ color: 'hsl(var(--foreground))' }}
                />
                {lifts.map((lift, index) => (
                  <Line 
                    key={lift}
                    type="monotone" 
                    dataKey={lift} 
                    stroke={colors[index % colors.length]} 
                    strokeWidth={isLiftVisible(lift) ? 2 : 1}
                    strokeOpacity={isLiftVisible(lift) ? 1 : 0.15}
                    dot={{ 
                      fill: colors[index % colors.length], 
                      strokeWidth: 2, 
                      r: isLiftVisible(lift) ? 3 : 1,
                      fillOpacity: isLiftVisible(lift) ? 1 : 0.15
                    }}
                    connectNulls
                  />
                ))}
              </LineChart>
            </ResponsiveContainer>
          </div>
          
          {/* Custom Interactive Legend */}
          <div className="flex flex-wrap gap-2 justify-center">
            {lifts.map((lift, index) => {
              const isActive = selectedLifts.size === 0 || selectedLifts.has(lift);
              return (
                <button
                  key={lift}
                  onClick={() => toggleLift(lift)}
                  className={cn(
                    "flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-medium transition-all",
                    "border hover:scale-105 active:scale-95",
                    isActive 
                      ? "bg-secondary/50 border-border/50 text-foreground" 
                      : "bg-secondary/20 border-border/30 text-muted-foreground opacity-50"
                  )}
                >
                  <span 
                    className="w-2.5 h-2.5 rounded-full" 
                    style={{ backgroundColor: colors[index % colors.length] }}
                  />
                  {lift}
                </button>
              );
            })}
            {selectedLifts.size > 0 && (
              <button
                onClick={() => setSelectedLifts(new Set())}
                className="px-3 py-1.5 rounded-full text-xs font-medium bg-primary/20 border border-primary/30 text-primary hover:bg-primary/30 transition-all"
              >
                Show All
              </button>
            )}
          </div>
        </section>
      )}
    </div>
  );
}
